'use client';

import * as React from 'react';

import { CommandPalette } from '@/components/command-palette/command-palette';
import { KeyboardMnemonics } from '@/components/command-palette/keyboard-mnemonics';
import { DockSlot } from '@/components/workspace/dock-slot';
import { useLayoutModeStore } from '@/lib/ui/layout-mode';

import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * The cockpit shell. Two layouts over the same pages, switched from the top bar
 * (persisted per browser — see `lib/ui/layout-mode`):
 *
 * - simple   — lifecycle sidebar + slim top bar.
 * - terminal — Kite-style: top nav + More, live index strip, docked watchlist.
 *
 * Wraps every route under `app/(app)/...`. ⌘K command palette mounts here once,
 * globally, so every page picks it up in either layout.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const mode = useLayoutModeStore((s) => s.mode);

  if (mode === 'terminal') {
    return (
      <div className="flex h-screen w-screen flex-col overflow-hidden bg-bg text-fg">
        <Topbar mode="terminal" />
        <div className="flex min-h-0 flex-1">
          <DockSlot />
          <main className="min-w-0 flex-1 overflow-auto">{children}</main>
        </div>
        <CommandPalette />
        <KeyboardMnemonics />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-bg text-fg">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar mode="simple" />
        <main className="min-h-0 flex-1 overflow-auto">{children}</main>
      </div>
      <CommandPalette />
      <KeyboardMnemonics />
    </div>
  );
}
